/**
 * The card Marvin's `markup_save` leaves in the conversation (`agent_action.result.card`, built by
 * `ai_engine/agent/actions/markups.py`): the sheet it marked, how many markups, and one row per markup.
 */
export type MarkupRowKind = "rect" | "cloud" | "arrow" | "text";

/** A point or box on the sheet, normalized 0..1 with the origin at the top-left (y down). */
export interface MarkupPoint {
    x: number;
    y: number;
}
export interface MarkupBox {
    xMin: number;
    yMin: number;
    xMax: number;
    yMax: number;
}

/** Where a markup sits: a box (rect, cloud, note) or an arrow's tail and head. */
export interface MarkupGeometry {
    bounds?: MarkupBox;
    start?: MarkupPoint;
    end?: MarkupPoint;
}

/**
 * How a markup is drawn, copied from its stored shape: `color` its stroke (absent on a card from before rows carried
 * one: Marvin's blue), `fillColor` and `fillOpacity` a highlighted area's translucent fill (absent: no fill).
 */
export interface MarkupAppearance {
    color?: string;
    fillColor?: string;
    fillOpacity?: number;
}

export interface MarkupCardRow extends MarkupAppearance {
    /** The markup's shape id; a labelled markup's text shape is decided with it. */
    shapeId: string;
    kind: MarkupRowKind;
    /** Its words (a label, or a note's text), when it has any. */
    label: string | null;
    /** Where it sits on the sheet; absent on a card from before the preview. */
    geometry?: MarkupGeometry;
}

export interface MarkupCard {
    kind: "markup";
    fileId: string;
    page: number;
    sheetId: string;
    /** The sheet's document, to open it in the viewer; absent on a card from before the preview. */
    documentId: string | null;
    /**
     * The revision the markups were saved under: with the file and page, the exact address of the user's draft.
     * The card reads and opens that address and never re-resolves one. Null on a card from before it was recorded.
     */
    revisionId: string | null;
    sheetLabel: string;
    count: number;
    rows: MarkupCardRow[];
}

const KINDS: readonly string[] = ["rect", "cloud", "arrow", "text"];

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const isUnit = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value);

const asPoint = (value: unknown): MarkupPoint | undefined =>
    isRecord(value) && isUnit(value.x) && isUnit(value.y) ? { x: value.x, y: value.y } : undefined;

const asBox = (value: unknown): MarkupBox | undefined =>
    isRecord(value) &&
    isUnit(value.xMin) &&
    isUnit(value.yMin) &&
    isUnit(value.xMax) &&
    isUnit(value.yMax)
        ? { xMin: value.xMin, yMin: value.yMin, xMax: value.xMax, yMax: value.yMax }
        : undefined;

function asGeometry(value: unknown): MarkupGeometry | undefined {
    if (!isRecord(value)) return undefined;
    const bounds = asBox(value.bounds);
    const start = asPoint(value.start);
    const end = asPoint(value.end);
    if (bounds) return { bounds };
    return start && end ? { start, end } : undefined;
}

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

const asHexColor = (value: unknown): string | undefined =>
    typeof value === "string" && HEX_COLOR.test(value) ? value : undefined;

/** A row's appearance. An invalid colour reads as absent; a fill is its colour and its opacity, both or neither. */
export function asMarkupAppearance(row: Record<string, unknown>): MarkupAppearance {
    const color = asHexColor(row.color);
    const fillColor = asHexColor(row.fillColor);
    const fillOpacity =
        isUnit(row.fillOpacity) && row.fillOpacity >= 0 && row.fillOpacity <= 1
            ? row.fillOpacity
            : undefined;
    return {
        ...(color ? { color } : {}),
        ...(fillColor && fillOpacity !== undefined ? { fillColor, fillOpacity } : {}),
    };
}

/** The result as a markup card, or null when it is not one (an older payload, a failure). */
export function asMarkupCard(result: unknown): MarkupCard | null {
    if (!isRecord(result) || result.kind !== "markup") return null;
    if (typeof result.fileId !== "string" || typeof result.sheetLabel !== "string") return null;
    if (typeof result.page !== "number" || !Array.isArray(result.rows)) return null;
    const rows = result.rows
        .filter(
            (row): row is Record<string, unknown> & { shapeId: string; kind: MarkupRowKind } =>
                isRecord(row) &&
                typeof row.shapeId === "string" &&
                typeof row.kind === "string" &&
                KINDS.includes(row.kind),
        )
        .map((row): MarkupCardRow => {
            const geometry = asGeometry(row.geometry);
            return {
                shapeId: row.shapeId,
                kind: row.kind,
                label: typeof row.label === "string" ? row.label : null,
                ...(geometry ? { geometry } : {}),
                ...asMarkupAppearance(row),
            };
        });
    return {
        kind: "markup",
        fileId: result.fileId,
        page: result.page,
        sheetId: typeof result.sheetId === "string" ? result.sheetId : "",
        documentId: typeof result.documentId === "string" ? result.documentId : null,
        revisionId: typeof result.revisionId === "string" ? result.revisionId : null,
        sheetLabel: result.sheetLabel,
        count: typeof result.count === "number" ? result.count : rows.length,
        rows,
    };
}

export const MARKUP_ROW_NOUNS: Record<MarkupRowKind, string> = {
    rect: "Box",
    cloud: "Cloud",
    arrow: "Arrow",
    text: "Note",
};

const EXTENT = (box: MarkupGeometry): MarkupBox | null => {
    if (box.bounds) return box.bounds;
    if (!box.start || !box.end) return null;
    return {
        xMin: Math.min(box.start.x, box.end.x),
        yMin: Math.min(box.start.y, box.end.y),
        xMax: Math.max(box.start.x, box.end.x),
        yMax: Math.max(box.start.y, box.end.y),
    };
};

/** The box that holds every markup on the card, or null when no row says where it sits. */
export function markupExtent(rows: readonly MarkupCardRow[]): MarkupBox | null {
    let found: MarkupBox | null = null;
    for (const row of rows) {
        const box = row.geometry ? EXTENT(row.geometry) : null;
        if (!box) continue;
        found = found
            ? {
                  xMin: Math.min(found.xMin, box.xMin),
                  yMin: Math.min(found.yMin, box.yMin),
                  xMax: Math.max(found.xMax, box.xMax),
                  yMax: Math.max(found.yMax, box.yMax),
              }
            : box;
    }
    return found;
}

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
const hundredth = (value: number) => Math.round(value * 100) / 100;

// The crop's shape on the page: wide enough to read and not a sliver, so one preview stays a modest height in
// the chat column whatever the markups cover.
const MIN_SIDE = 0.15;
const MIN_ASPECT = 0.75;
const MAX_ASPECT = 3;
const MARGIN = 0.06;

/**
 * The sheet region the preview shows for a card: the markups' extent with a margin, kept to a readable shape and
 * rounded to the hundredth the crop endpoint takes, so what is drawn over the crop lines up with the crop.
 * It uses every row, removed ones too, so a Keep or Remove redraws the shapes and never re-fetches the picture.
 */
export function previewRegion(rows: readonly MarkupCardRow[]): MarkupBox | null {
    const extent = markupExtent(rows);
    if (!extent) return null;
    let width = Math.max(extent.xMax - extent.xMin + 2 * MARGIN, MIN_SIDE);
    let height = Math.max(extent.yMax - extent.yMin + 2 * MARGIN, MIN_SIDE);
    if (width / height > MAX_ASPECT) height = width / MAX_ASPECT;
    if (width / height < MIN_ASPECT) width = height * MIN_ASPECT;
    width = Math.min(width, 1);
    height = Math.min(height, 1);
    const centerX = (extent.xMin + extent.xMax) / 2;
    const centerY = (extent.yMin + extent.yMax) / 2;
    const xMin = clamp01(Math.min(centerX - width / 2, 1 - width));
    const yMin = clamp01(Math.min(centerY - height / 2, 1 - height));
    return {
        xMin: hundredth(xMin),
        yMin: hundredth(yMin),
        xMax: hundredth(Math.min(1, xMin + width)),
        yMax: hundredth(Math.min(1, yMin + height)),
    };
}
